/**
 * Dark Mode Toggle Component
 * Switches between light and dark mode with smooth transition
 */

import { Moon, Sun } from 'lucide-react'
import { useState, useEffect } from 'react'
import { soundFx } from '../../lib/soundEffects'

export const DarkModeToggle = ({ className = '' }) => {
    const [isDark, setIsDark] = useState(false)

    // Check for saved preference or system preference on mount
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme')
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

        if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
            setIsDark(true)
            document.documentElement.setAttribute('data-theme', 'dark')
        }
    }, [])

    const toggleDarkMode = (e) => {
        if (e) e.audioplayed = true
        soundFx.play('click')
        const newIsDark = !isDark
        setIsDark(newIsDark)

        if (newIsDark) {
            document.documentElement.setAttribute('data-theme', 'dark')
            localStorage.setItem('theme', 'dark')
        } else {
            document.documentElement.removeAttribute('data-theme')
            localStorage.setItem('theme', 'light')
        }
    }

    return (
        <button
            onClick={toggleDarkMode}
            className={`
        relative inline-flex items-center gap-2
        px-4 py-2 rounded-xl
        glass-strong border border-white/30
        hover:scale-105 transition-all duration-200
        ${className}
      `.trim().replace(/\s+/g, ' ')}
            title={isDark ? 'Schakel naar licht thema' : 'Schakel naar donker thema'}
        >
            {/* Icon */}
            <div className="relative w-5 h-5">
                <Sun
                    className={`
            absolute inset-0 text-yellow-500
            transition-all duration-300
            ${isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}
          `.trim().replace(/\s+/g, ' ')}
                    size={20}
                />
                <Moon
                    className={`
            absolute inset-0 text-blue-400
            transition-all duration-300
            ${isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}
          `.trim().replace(/\s+/g, ' ')}
                    size={20}
                />
            </div>

            {/* Label */}
            <span className="text-sm font-medium text-gray-900">
                {isDark ? 'Donker' : 'Licht'}
            </span>
        </button>
    )
}

/**
 * Compact Dark Mode Toggle (Icon only)
 */
export const DarkModeToggleCompact = ({ className = '' }) => {
    const [isDark, setIsDark] = useState(false)

    useEffect(() => {
        const savedTheme = localStorage.getItem('theme')
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

        if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
            setIsDark(true)
            document.documentElement.setAttribute('data-theme', 'dark')
        }
    }, [])

    const toggleDarkMode = (e) => {
        if (e) e.audioplayed = true
        soundFx.play('click')
        const newIsDark = !isDark
        setIsDark(newIsDark)

        if (newIsDark) {
            document.documentElement.setAttribute('data-theme', 'dark')
            localStorage.setItem('theme', 'dark')
        } else {
            document.documentElement.removeAttribute('data-theme')
            localStorage.setItem('theme', 'light')
        }
    }

    return (
        <button
            onClick={toggleDarkMode}
            className={`
        relative p-2 rounded-xl
        glass-strong border border-white/30
        hover:scale-110 transition-all duration-200
        ${className}
      `.trim().replace(/\s+/g, ' ')}
            title={isDark ? 'Schakel naar licht thema' : 'Schakel naar donker thema'}
        >
            <div className="relative w-5 h-5">
                <Sun
                    className={`
            absolute inset-0 text-yellow-500
            transition-all duration-300
            ${isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}
          `.trim().replace(/\s+/g, ' ')}
                    size={20}
                />
                <Moon
                    className={`
            absolute inset-0 text-blue-400
            transition-all duration-300
            ${isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}
          `.trim().replace(/\s+/g, ' ')}
                    size={20}
                />
            </div>
        </button>
    )
}

/**
 * Hook to get/set dark mode
 */
export const useDarkMode = () => {
    const [isDark, setIsDark] = useState(() => {
        const saved = localStorage.getItem('theme')
        return saved === 'dark'
    })

    const toggleDarkMode = () => {
        const newIsDark = !isDark
        setIsDark(newIsDark)

        if (newIsDark) {
            document.documentElement.setAttribute('data-theme', 'dark')
            localStorage.setItem('theme', 'dark')
        } else {
            document.documentElement.removeAttribute('data-theme')
            localStorage.setItem('theme', 'light')
        }
    }

    return { isDark, toggleDarkMode }
}
